import { useTranslations } from 'next-intl'
import { MouseEvent } from 'react'

import { isNilOrEmptyString, useFormatDate } from '@/shared/lib'
import { Icon } from '@/shared/ui'

import { DeleteButton } from './delete-button.pc'
import { NoContent } from './no-content'
import { KeywordSearchHistory } from '../../model/keyword-search-history-store/history'
import { useKeywordSearchHistoryStore } from '../../model/keyword-search-history-store/store'
import { Item, List } from '../popup/common'

const DateRange = ({
  startDate,
  endDate,
}: {
  startDate: string | null
  endDate: string | null
}) => {
  const { formatDate } = useFormatDate()

  if (isNilOrEmptyString(startDate) || isNilOrEmptyString(endDate)) return null

  return (
    <p className="typo-caption1 font-medium text-gray-500">
      {formatDate({ date: startDate, format: 'yyyy.MM.dd' })}~
      {formatDate({ date: endDate, format: 'yyyy.MM.dd' })}
    </p>
  )
}

type Props = {
  onItemClick: (history: KeywordSearchHistory) => void
}

export const SearchHistoryList = ({ onItemClick }: Props) => {
  const t = useTranslations('search_panel')

  const defaultKeyword = t('recent_keyword.default_keyword')

  const searchHistory = useKeywordSearchHistoryStore(
    state => state.searchHistory,
  )
  const checkSearchHistoryExist = useKeywordSearchHistoryStore(
    state => state.checkSearchHistoryExist,
  )
  const searchHistoryActions = useKeywordSearchHistoryStore(
    state => state.actions,
  )
  const hasSearchHistory = checkSearchHistoryExist()

  const handleItemClick = (history: KeywordSearchHistory) => () => {
    onItemClick(history)
  }

  const handleDeleteItem = (id: string) => (event: MouseEvent) => {
    event.stopPropagation()
    searchHistoryActions.deleteSearchHistory(id)
  }

  const handleDeleteAll = () => {
    searchHistoryActions.clearSearchHistory()
  }

  return (
    <List
      title={t('recent_keyword.title')}
      className="relative w-[400px]"
      height={288}
    >
      {hasSearchHistory && <DeleteButton onClick={handleDeleteAll} />}
      {(() => {
        if (!hasSearchHistory)
          return <NoContent>{t('recent_keyword.empty')}</NoContent>

        return searchHistory.map(history => (
          <Item
            key={history.id}
            onClick={handleItemClick(history)}
            data-item-type="search-history"
          >
            <div className="flex gap-2">
              <Icon
                name="clock"
                size={18}
                className="relative top-[3px] text-gray-400"
              />
              <div className="space-y-1">
                <p>{history.keyword || defaultKeyword}</p>
                <DateRange
                  startDate={history.startDate}
                  endDate={history.endDate}
                />
              </div>
            </div>
            <div role="button" onClick={handleDeleteItem(history.id)}>
              <Icon name="close" size={16} className="text-gray-500" />
            </div>
          </Item>
        ))
      })()}
    </List>
  )
}
